/**
 * AnalyzerCard — the free LinkedIn Profile Analyzer (D4 lead magnet).
 *
 * Paste headline / About / experience text + a target role; the AI returns a
 * five-dimension scored critique with a concrete rewrite per dimension. Works
 * logged-out (anonymous callers hit a per-IP daily cap on the backend; a 429
 * renders an inline upsell). Mirrors ATSScorePage's score-card visual pattern.
 *
 * Paste-text only — there is no URL input anywhere in this component.
 */

import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";
import {
  analyzeLinkedInProfile,
  type LinkedInAnalyzeResult,
  type LinkedInDimensionKey,
} from "../../api/client";
import { track } from "../../utils/analytics";

const DIMENSION_LABEL_KEYS: Record<LinkedInDimensionKey, string> = {
  headline_clarity: "AnalyzerCard.dimensionHeadlineClarity",
  keyword_alignment: "AnalyzerCard.dimensionKeywordAlignment",
  story_coherence: "AnalyzerCard.dimensionStoryCoherence",
  credibility_signals: "AnalyzerCard.dimensionCredibilitySignals",
  cta_strength: "AnalyzerCard.dimensionCtaStrength",
};

const CAPS = { headline: 300, about: 4000, experience: 8000, target_role: 120 };

function scoreColor(theme: Theme, score: number): string {
  if (score >= 75) return theme.palette.success.dark;
  if (score >= 50) return theme.palette.warning.main;
  return theme.palette.error.dark;
}

export default function AnalyzerCard() {
  const theme = useTheme();
  const { t } = useTranslation("linkedin");
  const navigate = useNavigate();

  const [headline, setHeadline] = useState("");
  const [about, setAbout] = useState("");
  const [experience, setExperience] = useState("");
  const [targetRole, setTargetRole] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<LinkedInAnalyzeResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [capped, setCapped] = useState(false);

  const canSubmit =
    !!headline.trim() && !!about.trim() && !!experience.trim() && !!targetRole.trim();

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    setError(null);
    setCapped(false);
    try {
      const data = await analyzeLinkedInProfile({
        headline,
        about,
        experience,
        target_role: targetRole,
      });
      setResult(data);
      track("linkedin.analyze_run", { target_role: targetRole });
    } catch (e: any) {
      if (e?.response?.status === 429) {
        setCapped(true);
        track("linkedin.anon_cap_hit");
      } else {
        const detail = e?.response?.data?.detail;
        setError(
          (typeof detail === "string" && detail) ||
            t("AnalyzerCard.errorFallback"),
        );
      }
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return <AnalyzerResult result={result} onReset={() => setResult(null)} />;
  }

  return (
    <Container maxWidth="md" disableGutters>
      <Paper
        elevation={0}
        sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: { xs: 3, md: 4 }, mb: 3 }}
      >
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          <AutoAwesomeIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
          <Typography variant="h6" fontWeight={500}>
            {t("AnalyzerCard.title")}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
          {t("AnalyzerCard.intro")}
        </Typography>

        <Stack spacing={2.5}>
          <Field
            label={t("AnalyzerCard.headlineLabel")}
            value={headline}
            onChange={setHeadline}
            max={CAPS.headline}
            disabled={loading}
            placeholder={t("AnalyzerCard.headlinePlaceholder")}
          />
          <Field
            label={t("AnalyzerCard.targetRoleLabel")}
            value={targetRole}
            onChange={setTargetRole}
            max={CAPS.target_role}
            disabled={loading}
            placeholder={t("AnalyzerCard.targetRolePlaceholder")}
          />
          <Field
            label={t("AnalyzerCard.aboutLabel")}
            value={about}
            onChange={setAbout}
            max={CAPS.about}
            disabled={loading}
            multiline
            minRows={5}
            placeholder={t("AnalyzerCard.aboutPlaceholder")}
          />
          <Field
            label={t("AnalyzerCard.experienceLabel")}
            value={experience}
            onChange={setExperience}
            max={CAPS.experience}
            disabled={loading}
            multiline
            minRows={6}
            placeholder={t("AnalyzerCard.experiencePlaceholder")}
          />
        </Stack>

        {capped && (
          <Alert
            severity="info"
            sx={{ mt: 3, borderRadius: 2 }}
            action={
              <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate("/pricing")}>
                {t("AnalyzerCard.seeAllAccessButton")}
              </Button>
            }
          >
            {t("AnalyzerCard.cappedMessage")}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 3, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Stack direction="row" justifyContent="flex-end" mt={3}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={loading || !canSubmit}
            sx={{ fontWeight: 500, px: 4, textTransform: "none" }}
          >
            {loading ? t("AnalyzerCard.analyzingButton") : t("AnalyzerCard.analyzeButton")}
          </Button>
        </Stack>
        {loading && (
          <LinearProgress
            sx={{ mt: 2, height: 6, borderRadius: 3, "& .MuiLinearProgress-bar": { bgcolor: theme.palette.primary.light } }}
          />
        )}
      </Paper>
    </Container>
  );
}

function Field({
  label,
  value,
  onChange,
  max,
  disabled,
  placeholder,
  multiline,
  minRows,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  max: number;
  disabled: boolean;
  placeholder?: string;
  multiline?: boolean;
  minRows?: number;
}) {
  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={500} gutterBottom>
        {label}
      </Typography>
      <TextField
        fullWidth
        multiline={multiline}
        minRows={minRows}
        value={value}
        onChange={(e) => onChange(e.target.value.slice(0, max))}
        disabled={disabled}
        placeholder={placeholder}
        inputProps={{ maxLength: max, "aria-label": label }}
      />
      <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 0.5 }}>
        {value.length.toLocaleString()} / {max.toLocaleString()}
      </Typography>
    </Box>
  );
}

function AnalyzerResult({
  result,
  onReset,
}: {
  result: LinkedInAnalyzeResult;
  onReset: () => void;
}) {
  const theme = useTheme();
  const { t } = useTranslation("linkedin");
  const heroColor = scoreColor(theme, result.overall);

  return (
    <Container maxWidth="md" disableGutters>
      <Stack spacing={3}>
        <Paper
          elevation={0}
          sx={{ bgcolor: alpha(heroColor, 0.05), border: `1px solid ${alpha(heroColor, 0.3)}`, borderRadius: 3, p: 4 }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} alignItems="center" spacing={3}>
            <Box textAlign={{ xs: "center", sm: "left" }}>
              <Typography variant="caption" fontWeight={700} sx={{ color: heroColor, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                {t("AnalyzerCard.overallScoreLabel")}
              </Typography>
              <Typography variant="h1" fontWeight={900} sx={{ color: heroColor, letterSpacing: "-0.03em", lineHeight: 1 }}>
                {result.overall}%
              </Typography>
            </Box>
            <Box flex={1}>
              <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                {result.summary}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {result.dimensions.map((d) => (
          <DimensionCard key={d.key} dimension={d} />
        ))}

        <Stack direction="row" spacing={2}>
          <Button variant="outlined" onClick={onReset} sx={{ fontWeight: 500, textTransform: "none" }}>
            {t("AnalyzerCard.analyzeAnotherButton")}
          </Button>
        </Stack>
      </Stack>
    </Container>
  );
}

function DimensionCard({
  dimension,
}: {
  dimension: LinkedInAnalyzeResult["dimensions"][number];
}) {
  const theme = useTheme();
  const { t } = useTranslation("linkedin");
  const [copied, setCopied] = useState(false);
  const color = scoreColor(theme, dimension.score);

  const handleCopy = () => {
    navigator.clipboard.writeText(dimension.rewrite);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <Paper
      elevation={0}
      sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: 3 }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.75}>
        <Typography variant="subtitle1" fontWeight={500}>
          {t(DIMENSION_LABEL_KEYS[dimension.key])}
        </Typography>
        <Typography variant="body2" fontWeight={500} sx={{ color }}>
          {dimension.score}%
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={dimension.score}
        sx={{ height: 6, borderRadius: 3, mb: 1.5, "& .MuiLinearProgress-bar": { bgcolor: color } }}
      />
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, mb: 2 }}>
        {dimension.reading}
      </Typography>
      <Paper
        elevation={0}
        sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`, borderRadius: 2, p: 2 }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="caption" fontWeight={700} sx={{ color: theme.palette.primary.main, letterSpacing: "0.05em", textTransform: "uppercase" }}>
            {t("AnalyzerCard.suggestedRewriteLabel")}
          </Typography>
          <Tooltip title={copied ? t("AnalyzerCard.copiedTooltip") : t("AnalyzerCard.copyRewriteTooltip")}>
            <IconButton
              size="small"
              aria-label={t("AnalyzerCard.copyRewriteAriaLabel", { dimension: t(DIMENSION_LABEL_KEYS[dimension.key]) })}
              onClick={handleCopy}
              sx={{ color: copied ? theme.palette.success.dark : theme.palette.primary.main }}
            >
              {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Stack>
        <Box
          component="pre"
          sx={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: "0.8rem", lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word", m: 0 }}
        >
          {dimension.rewrite}
        </Box>
      </Paper>
    </Paper>
  );
}
